// src/pages/Connector/PrayerTiming/PrayerTimingCalendar.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../../utils/axiosInstance";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Chip,
    Tooltip
} from '@mui/material';
import {
    Edit as EditIcon,
    Refresh as RefreshIcon,
    NavigateBefore,
    NavigateNext,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import EditPrayerTimeDialog from './EditPrayerTimeDialog';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

export default function PrayerTimingCalendar({ open, onClose, config, chatbotId, canModify, onFetch }) {
    const [prayerTimes, setPrayerTimes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [editingPrayerTime, setEditingPrayerTime] = useState(null);
    const [isFetching, setIsFetching] = useState(false);

    // Generate year options (current year - 1 to current year + 2)
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 4 }, (_, i) => currentYear - 1 + i);

    // Fetch prayer times for selected month/year
    const fetchPrayerTimes = useCallback(async () => {
        if (!config || !open) return;

        setIsLoading(true);
        setError('');

        try {
            const response = await axiosInstance.get(
                `/api/prayer-timing/configurations/${config.id}/times`,
                {
                    params: {
                        chatbot_id: chatbotId,
                        month: selectedMonth,
                        year: selectedYear
                    }
                }
            );
            setPrayerTimes(response.data.data?.prayer_times || []);
        } catch (err) {
            console.error("Error fetching prayer times:", err);
            setError(err.response?.data?.message || "Failed to fetch prayer times.");
        } finally {
            setIsLoading(false);
        }
    }, [config, chatbotId, selectedMonth, selectedYear, open]);

    useEffect(() => {
        fetchPrayerTimes();
    }, [fetchPrayerTimes]);

    // Handle fetch from API
    const handleFetchFromAPI = async () => {
        setIsFetching(true);
        try {
            await onFetch(config.id, selectedMonth, selectedYear);
            // Refresh the list after fetching
            await fetchPrayerTimes();
        } finally {
            setIsFetching(false);
        }
    };

    // Navigate months
    const handlePreviousMonth = () => {
        if (selectedMonth === 1) {
            setSelectedMonth(12);
            setSelectedYear(prev => prev - 1);
        } else {
            setSelectedMonth(prev => prev - 1);
        }
    };

    const handleNextMonth = () => {
        if (selectedMonth === 12) {
            setSelectedMonth(1);
            setSelectedYear(prev => prev + 1);
        } else {
            setSelectedMonth(prev => prev + 1);
        }
    };

    // Handle edit
    const handleEdit = (prayerTime) => {
        setEditingPrayerTime(prayerTime);
    };

    const handleCloseEdit = () => {
        setEditingPrayerTime(null);
    };

    const handleSaveEdit = async (updatedData) => {
        try {
            await axiosInstance.put(
                `/api/prayer-timing/times/${editingPrayerTime.id}`,
                updatedData,
                { params: { chatbot_id: chatbotId } }
            );
            handleCloseEdit();
            fetchPrayerTimes();
        } catch (err) {
            throw err;
        }
    };

    // Format date for display
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };

    // Download PDF
    const handleDownloadPDF = () => {
        const doc = new jsPDF('landscape');

        // Title
        doc.setFontSize(18);
        doc.text(`Prayer Times - ${config?.title}`, 14, 22);
        doc.setFontSize(12);
        doc.text(`${MONTHS[selectedMonth - 1]} ${selectedYear}`, 14, 30);
        if (config?.city && config?.country) {
            doc.text(`Location: ${config.city}, ${config.country}`, 14, 38);
        }

        // Table data
        const tableData = prayerTimes.map(pt => [
            formatDate(pt.date),
            pt.hijri_date || '-',
            pt.fajr || '-',
            pt.sunrise || '-',
            pt.dhuhr || '-',
            pt.asr || '-',
            pt.asr_hanafi || '-',
            pt.maghrib || '-',
            pt.isha || '-'
        ]);

        // Generate table
        autoTable(doc, {
            head: [['Date', 'Hijri', 'Fajr', 'Sunrise', 'Dhuhr', 'Asr (Shafi\'i)', 'Asr (Hanafi)', 'Maghrib', 'Isha']],
            body: tableData,
            startY: 45,
            styles: { fontSize: 9 },
            headStyles: { fillColor: [41, 128, 185] }
        });

        // Save
        doc.save(`prayer-times-${config?.title}-${MONTHS[selectedMonth - 1]}-${selectedYear}.pdf`);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Prayer Times - {config?.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {prayerTimes.length > 0 && (
                            <Tooltip title="Download PDF">
                                <IconButton
                                    color="primary"
                                    onClick={handleDownloadPDF}
                                    size="small"
                                >
                                    <PdfIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canModify && (
                            <Button
                                variant="outlined"
                                startIcon={isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
                                onClick={handleFetchFromAPI}
                                disabled={isFetching}
                                size="small"
                            >
                                Fetch from API
                            </Button>
                        )}
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* Month/Year Selection */}
                <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
                    <Grid item>
                        <IconButton onClick={handlePreviousMonth}>
                            <NavigateBefore />
                        </IconButton>
                    </Grid>
                    <Grid item xs>
                        <Grid container spacing={2} justifyContent="center">
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Month</InputLabel>
                                    <Select
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(e.target.value)}
                                        label="Month"
                                    >
                                        {MONTHS.map((month, index) => (
                                            <MenuItem key={index} value={index + 1}>
                                                {month}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Year</InputLabel>
                                    <Select
                                        value={selectedYear}
                                        onChange={(e) => setSelectedYear(e.target.value)}
                                        label="Year"
                                    >
                                        {yearOptions.map((year) => (
                                            <MenuItem key={year} value={year}>
                                                {year}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <IconButton onClick={handleNextMonth}>
                            <NavigateNext />
                        </IconButton>
                    </Grid>
                </Grid>

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : prayerTimes.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Prayer Times Found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            No prayer times available for {MONTHS[selectedMonth - 1]} {selectedYear}.
                        </Typography>
                        {canModify && (
                            <Button
                                variant="contained"
                                startIcon={<RefreshIcon />}
                                onClick={handleFetchFromAPI}
                                disabled={isFetching}
                            >
                                Fetch Prayer Times
                            </Button>
                        )}
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Date</strong></TableCell>
                                    <TableCell><strong>Hijri</strong></TableCell>
                                    <TableCell align="center"><strong>Fajr</strong></TableCell>
                                    <TableCell align="center"><strong>Sunrise</strong></TableCell>
                                    <TableCell align="center"><strong>Dhuhr</strong></TableCell>
                                    <TableCell align="center"><strong>Asr (Shafi'i)</strong></TableCell>
                                    <TableCell align="center"><strong>Asr (Hanafi)</strong></TableCell>
                                    <TableCell align="center"><strong>Maghrib</strong></TableCell>
                                    <TableCell align="center"><strong>Isha</strong></TableCell>
                                    <TableCell align="center"><strong>Status</strong></TableCell>
                                    {canModify && <TableCell align="center"><strong>Edit</strong></TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {prayerTimes.map((pt) => (
                                    <TableRow key={pt.id} hover>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(pt.date)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {pt.hijri_date || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">{pt.fajr || '-'}</TableCell>
                                        <TableCell align="center">{pt.sunrise || '-'}</TableCell>
                                        <TableCell align="center">{pt.dhuhr || '-'}</TableCell>
                                        <TableCell align="center">{pt.asr || '-'}</TableCell>
                                        <TableCell align="center">{pt.asr_hanafi || '-'}</TableCell>
                                        <TableCell align="center">{pt.maghrib || '-'}</TableCell>
                                        <TableCell align="center">{pt.isha || '-'}</TableCell>
                                        <TableCell align="center">
                                            {pt.is_trained ? (
                                                <Chip label="Trained" color="success" size="small" />
                                            ) : (
                                                <Chip label="Pending" size="small" />
                                            )}
                                        </TableCell>
                                        {canModify && (
                                            <TableCell align="center">
                                                <Tooltip title="Edit Prayer Time">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEdit(pt)}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>

            {/* Edit Prayer Time Dialog */}
            {editingPrayerTime && (
                <EditPrayerTimeDialog
                    open={!!editingPrayerTime}
                    onClose={handleCloseEdit}
                    onSave={handleSaveEdit}
                    prayerTime={editingPrayerTime}
                />
            )}
        </Dialog>
    );
}
